import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Check, X, Mail, Phone, ArrowLeft, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUS_STYLES = {
  pending: "bg-amber-400/15 text-amber-400",
  approved: "bg-emerald-400/15 text-emerald-400",
  denied: "bg-red-400/15 text-red-400",
};

/** Host-facing tracking/analytics + approval view for everyone who registered
 * for a "Require Registration" meeting — see MeetingRegisterPage for the
 * public-facing counterpart attendees fill out. */
export default function MeetingRegistrationsPage() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get(`/meetings/${meetingId}/registrations`).then(({ data }) => setRegistrations(data)).catch((e) => {
    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load registrations");
  });
  useEffect(() => { load(); }, [meetingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (regId, action) => {
    setBusyId(regId);
    try {
      await api.post(`/meetings/${meetingId}/registrations/${regId}/${action}`);
      toast.success(action === "approve" ? "Registrant approved" : "Registrant denied");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8" data-testid="meeting-registrations-page">
      <button onClick={() => navigate("/meetings/all")} data-testid="registrations-back-btn" className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Meetings
      </button>
      <h1 className="font-heading text-2xl font-semibold text-white flex items-center gap-2"><UserPlus className="w-5 h-5 text-fuchsia-400" /> Registrations</h1>
      <p className="text-sm text-slate-400 mt-1">Everyone who registered for this meeting, and their approval status.</p>

      {!registrations ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
      ) : registrations.length === 0 ? (
        <div className="glass-panel rounded-2xl p-8 text-center text-sm text-slate-400 mt-6">No registrations yet — share your registration link to start collecting them.</div>
      ) : (
        <div className="glass-panel rounded-2xl divide-y divide-white/5 mt-6" data-testid="registrations-list">
          {registrations.map((r) => (
            <div key={r.id} className="flex items-center gap-3 p-4 flex-wrap" data-testid={`registration-row-${r.id}`}>
              <div className="w-9 h-9 rounded-full bg-fuchsia-500/20 flex items-center justify-center text-fuchsia-300 font-bold text-sm shrink-0">{r.name[0]?.toUpperCase()}</div>
              <div className="min-w-[160px]">
                <p className="text-sm font-semibold text-white">{r.name}</p>
                <p className="text-xs text-slate-400 flex items-center gap-1"><Mail className="w-3 h-3" /> {r.email}</p>
              </div>
              <p className="text-xs text-slate-400 flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone}</p>
              {Object.entries(r.custom_fields || {}).map(([k, v]) => (
                <span key={k} className="text-xs text-slate-400 bg-white/5 px-2 py-1 rounded">{v}</span>
              ))}
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_STYLES[r.status]}`}>{r.status}</span>
              <div className="ml-auto flex items-center gap-2">
                {r.status === "pending" && (
                  <>
                    <button onClick={() => act(r.id, "approve")} disabled={busyId === r.id} data-testid={`registration-approve-btn-${r.id}`}
                      className="flex items-center gap-1 text-xs font-semibold bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 px-2.5 py-1.5 rounded-lg disabled:opacity-60"><Check className="w-3 h-3" /> Approve</button>
                    <button onClick={() => act(r.id, "deny")} disabled={busyId === r.id} data-testid={`registration-deny-btn-${r.id}`}
                      className="flex items-center gap-1 text-xs font-semibold bg-red-500/20 text-red-300 hover:bg-red-500/30 px-2.5 py-1.5 rounded-lg disabled:opacity-60"><X className="w-3 h-3" /> Deny</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
